import { X, Pencil } from "lucide-react";
import { useState, useEffect } from "react";
import toast from "react-hot-toast";
import CamposAgendamento from "./CamposAgendamento";
import CamposTecnico from "./CamposTecnico";
import CampoRelatoVisita from "./CampoRelatoVisita";
import MenuAgendamento from "./MenuAgendamento";

const API_URL = process.env.REACT_APP_API_URL;

export default function FormEditarAgendamento({ chamado, onVoltar, onAtualizar }) {
  const [data, setData] = useState("");
  const [hora, setHora] = useState("");
  const [dataFinalizado, setDataFinalizado] = useState("");
  const [empresa, setEmpresa] = useState("");
  const [colaborador, setColaborador] = useState("");
  const [tecnico, setTecnico] = useState("");
  const [tipoServico, setTipoServico] = useState("");
  const [observacao, setObservacao] = useState("");
  const [relato, setRelato] = useState("");
  const [status, setStatus] = useState("Pendente");
  const [clientes, setClientes] = useState([]);

  // 🔹 Buscar clientes (pra dropdown)
  useEffect(() => {
    fetch(`${API_URL}/cliente/listar.php`)
      .then((r) => (r.headers.get("content-type")?.includes("json") ? r.json() : null))
      .then((json) => {
        if (json?.dados && Array.isArray(json.dados)) setClientes(json.dados);
        else setClientes([]);
      })
      .catch(() => setClientes([]));
  }, []);

  // 🔹 Preenche os campos se for edição
  useEffect(() => {
    if (chamado) {
      setData(chamado.data_abertura || "");
      setHora(chamado.hora_abertura || "");
      setDataFinalizado(
        chamado.finalizado_em && chamado.finalizado_em !== "0000-00-00"
          ? chamado.finalizado_em
          : ""
      );
      setEmpresa(chamado.empresa || "");
      setColaborador(chamado.colaborador || "");
      setTecnico(chamado.tecnico_responsavel || "");
      setTipoServico(chamado.tipo_servico || "");
      setObservacao(chamado.observacao || "");
      setRelato(chamado.relato || "");
      setStatus(chamado.status || "Pendente");
    }
  }, [chamado]);

  // 🔹 Salvar (editar ou novo)
  const handleSalvar = async () => {
    try {
      const response = await fetch(`${API_URL}/agendamento/inserir.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: chamado?.id || null,
          data,
          hora,
          dataFinalizado,
          empresa,
          responsavelEmpresa: colaborador,
          tecnico,
          servico: tipoServico,
          observacao,
          relato,
          status,
          linkAnexado: null,
        }),
      });

      const result = await response.json();

      if (result?.id || result?.status === true) {
        toast.success("✅ Agendamento salvo com sucesso!");
        onAtualizar?.();
        onVoltar();
      } else {
        toast.error("❌ Erro ao salvar agendamento!");
        console.error("Erro:", result);
      }
    } catch (err) {
      console.error("Erro:", err);
      toast.error("❌ Falha na conexão com o servidor!");
    }
  };

  // 🔹 Finalizar agendamento
  const handleFinalizar = async () => {
    const hoje = new Date().toISOString().split("T")[0];
    setStatus("Concluído");
    setDataFinalizado(hoje);

    try {
      const response = await fetch(`${API_URL}/agendamento/inserir.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: chamado?.id || null,
          data,
          hora,
          dataFinalizado: hoje,
          empresa,
          responsavelEmpresa: colaborador,
          tecnico,
          servico: tipoServico,
          observacao,
          relato,
          status: "Concluído",
          linkAnexado: null,
        }),
      });

      const result = await response.json();

      if (result?.id || result?.status === true) {
        toast.success("🎉 Agendamento concluído!");
        onAtualizar?.();
        onVoltar();
      } else {
        toast.error("❌ Erro ao finalizar agendamento!");
      }
    } catch (err) {
      console.error("Erro:", err);
      toast.error("❌ Falha na conexão com servidor!");
    }
  };

  return (
    <div className="flex flex-col h-full bg-white rounded-md shadow-md border border-gray-300 overflow-hidden">
      {/* 🔹 Cabeçalho */}
      <div className="flex items-center justify-between px-4 py-3 bg-[#2b2b2b]">
        <div className="flex items-center gap-2">
          <Pencil size={20} className="text-white" />
          <h2 className="text-lg font-semibold text-white">
            {chamado ? "Editar Agendamento" : "Novo Agendamento"}
          </h2>
        </div>
        <button
          onClick={onVoltar}
          className="w-8 h-8 flex items-center justify-center rounded-full bg-white text-black 
                   transition hover:scale-110 hover:bg-slate-200"
        >
          <X size={16} />
        </button>
      </div>

      {/* 🔹 Corpo */}
      <div className="flex flex-1 overflow-hidden">
        {/* 🔸 Conteúdo principal */}
        <div className="flex-1 p-6 overflow-y-auto space-y-10">
          <CamposAgendamento
            data={data}
            setData={setData}
            hora={hora}
            setHora={setHora}
            dataFinalizado={dataFinalizado}
            setDataFinalizado={setDataFinalizado}
            empresa={empresa}
            setEmpresa={setEmpresa}
            clientes={clientes || []}
          />

          <CamposTecnico
            colaborador={colaborador}
            setColaborador={setColaborador}
            tecnico={tecnico}
            setTecnico={setTecnico}
            tipoServico={tipoServico}
            setTipoServico={setTipoServico}
          />

          {/* 🔸 Campo com espaçamento ajustado */}
          <CampoRelatoVisita
            observacoes={observacao}
            setObservacoes={setObservacao}
            relatoVisita={relato}
            setRelatoVisita={setRelato}
          />
        </div>

        {/* 🔸 Menu lateral */}
        <div className="w-48 border-l border-slate-300 bg-gray-50 flex flex-col justify-center items-stretch p-4 gap-3">
          <MenuAgendamento
            onSalvar={handleSalvar}
            onCancelar={onVoltar}
            onFinalizar={handleFinalizar}
            status={status}
            setStatus={setStatus}
          />
        </div>
      </div>
    </div>
  );
}
